import { useEffect } from 'react';

import { License } from '~/main/environments';
import { CFD_EVENTS, ELECTRON_EVENTS, HUB_EVENTS, PRINTER_EVENTS } from '~/main/namespace';
import { Options } from '~/renderer/store/cfd/types';

import type {
  ExecutionTimes,
  LABEL_SIZES,
  PRINTER_BRANDS,
  PRINTER_CONNECTIONS,
  PRINTER_FORMATS,
  PRINTER_KINDS,
} from '~/main/printer/types';
import type { ProgressInfo, UpdateInfo } from 'builder-util-runtime';

interface LicenceArg {
  license: License;
}

export type PrintEventData = {
  kind: PRINTER_KINDS;
  subkind: string;
  brand: PRINTER_BRANDS;
  format: PRINTER_FORMATS;
  model: string;
  size?: LABEL_SIZES;
  isTestPrint: boolean;
  connection: PRINTER_CONNECTIONS;
  executionTimes: ExecutionTimes;
  executionTime?: number;
};

type CallBack<T = undefined, K = undefined> = (event: Electron.Event, arg1: T, arg2: K) => void;

export function useEventListener(
  name: HUB_EVENTS.LICENSE_BODY_READY,
  cb: CallBack<LicenceArg>,
): void;
export function useEventListener(name: HUB_EVENTS.OPEN_PREFERENCES, cb: CallBack): void;
export function useEventListener(
  name: HUB_EVENTS.LOCAL_SERVER_CONNECTION_CHANGED,
  cb: CallBack<boolean>,
): void;
export function useEventListener(
  name: HUB_EVENTS.QUERY_PARAMS,
  cb: CallBack<
    { params: Partial<Options & { channelName: string; routeTo: string }> },
    { params: string }
  >,
): void;
export function useEventListener(
  name: ELECTRON_EVENTS.UPDATE_ERROR,
  cb: CallBack<Error, boolean>,
): void;
export function useEventListener(
  name: ELECTRON_EVENTS.UPDATE_NOT_AVAILABLE,
  cb: CallBack<{ info: UpdateInfo; isManual: boolean }>,
): void;
export function useEventListener(
  name: ELECTRON_EVENTS.UPDATE_AVAILABLE,
  cb: CallBack<{ info: UpdateInfo; isManual: boolean }>,
): void;
export function useEventListener(
  name: ELECTRON_EVENTS.DOWNLOAD_PROGRESS,
  cb: CallBack<{ progress: ProgressInfo }>,
): void;
export function useEventListener(
  name: ELECTRON_EVENTS.UPDATE_DOWNLOADED,
  cb: CallBack<{ isManual: boolean }>,
): void;

export function useEventListener(name: HUB_EVENTS.LICENSE_UPDATED, cb: CallBack<LicenceArg>): void;
export function useEventListener(
  name: HUB_EVENTS.LOCAL_SERVER_RECEIVE,
  cb: CallBack<string, unknown>,
): void;
export function useEventListener(name: PRINTER_EVENTS.SCAN_FAILED, cb: CallBack<Error>): void;
export function useEventListener(
  name: PRINTER_EVENTS.PRINT_ERROR,
  cb: CallBack<string, PrintEventData | undefined>,
): void;
export function useEventListener(
  name: PRINTER_EVENTS.PRINT_COMPLETED,
  cb: CallBack<PrintEventData>,
): void;
export function useEventListener(
  name: HUB_EVENTS.RELAY_PUBLISH,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cb: CallBack<{ message: string; data: any }>,
): void;
export function useEventListener(name: CFD_EVENTS.ALLOW_SINGLE_SCREEN, cb: CallBack<boolean>): void;
export function useEventListener(name: string, cb: CallBack): void;
export function useEventListener(
  eventName: HUB_EVENTS | ELECTRON_EVENTS | PRINTER_EVENTS | CFD_EVENTS | string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callback: (event: Electron.IpcRendererEvent, ...args: any[]) => void,
): void {
  useEffect(() => {
    if (!window.ipcRenderer) return;

    window.ipcRenderer.on(eventName, callback);
    return () => {
      window.ipcRenderer.removeListener(eventName, callback);
    };
  }, [eventName, callback]);
}
